import { apiErrorMessage } from '@agos/api-client'
import { useGetCustomers, useGetReferrals } from '@agos/api-client/customers'
import {
  getGetCampaignsQueryKey,
  getGetCampaignsSendCapQueryKey,
  getGetCampaignsWheelAdminQueryKey,
  useDeleteCampaignsIdId,
  useGetCampaigns,
  useGetCampaignsSendCap,
  useGetCampaignsWheelAdmin,
  useGetLoyaltyLeaderboard,
  useGetLoyaltyLeaderboardHistory,
  usePatchCampaignsIdId,
  usePatchCampaignsSendCap,
  usePatchCampaignsWheel,
  usePatchCampaignsType,
  usePostCampaigns,
  usePostCampaignsTypeRun,
} from '@agos/api-client/marketing'
import { T, formatCents, formatCount, formatDateTime, formatPercent, scheduledJobsOffNote, tt, useFranchiseModules } from '@agos/shared'
import { ConfirmDialog, EmptyState, Spinner, StatusBadge, cn, useToast, BUTTON_PRIMARY} from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import { AlertCircle, AlertTriangle, Gauge, Info, Play, Trash2, Trophy, Users, X } from 'lucide-react'
import { Fragment, useMemo, useState } from 'react'

import {
  MINI_GAME_TYPES,
  WHEEL_PLACEHOLDER_TYPE,
  activeCount,
  campaignLabel,
  campaignName,
  campaignsOf,
  campaignWindow,
  creatableTypes,
  ORGANIZATION_CASHBACK_GROUP,
  retiredCashbackStillOn,
  windowChanged,
  windowFormOf,
  windowNote,
  windowPayload,
  windowProblems,
  type CampaignWindowForm,
  capHistoryNote,
  capMessage,
  capPayload,
  capProblems,
  capState,
  creationNote,
  EMPTY_NEW_CAMPAIGN,
  groupDescription,
  groupedCampaigns,
  groupTitle,
  hasSegment,
  isNamedCampaign,
  isLeaderboardMonth,
  latestLeaderboardMonth,
  leaderboardHistoryOf,
  leaderboardOf,
  deleteRefusalNote,
  mayDelete,
  mayRunByHand,
  newCampaignPayload,
  newCampaignProblems,
  redemptionLabel,
  redemptionNote,
  referralsOf,
  referralStatusesWorthFiltering,
  referralTotals,
  runMessage,
  runOutcome,
  sendCapOf,
  totalSent,
  TORNADO_TRIGGERS,
  tornadoTriggerLabel,
  tornadoTriggerMessage,
  triggerMessage,
  triggerOf,
  typesWithSeveral,
  ungroupedCampaigns,
  wheelConditions,
  wheelLoseChance,
  wheelNote,
  wheelOdds,
  wheelFormOf,
  wheelPayload,
  wheelProblemMessage,
  wheelProblems,
  weightOdds,
  type WheelForm,
  wheelOf,
  type Campaign,
  type NewCampaign,
  type RunOutcome,
  campaignFieldLabel,
  campaignFields,
  inertNote,
  isKnownField,
  campaignFieldLabelForChannel,
  smsConfirmPhrase,
  smsConfirmed,
  smsSwitchWarning,
  changedSettings,
  channelsOf,
  changedMessages,
  channelModeLabel,
  channelModeOf,
  messageText,
  messageTextKeys,
  placeholderList,
  byHandText,
  hasByHandTexts,
  BY_HAND_TEXT_KEYS,
  MESSAGE_TEXT_KEYS,
  type ChannelMode,
  type MessageTextKey,
  unshownSettingsNote,
  parseFieldValue,
} from './campaigns'
import { sendKindOf } from './campaign-send'
import { MessageTextBox } from './MessageTextBox'
import { OrganizationCashbackRow } from './OrganizationCashbackSection'
import { SendNowPanel } from './SendNowPanel'
import {
  SLOT_REWARDS,
  newSlot,
  slotFormOf,
  slotProblemMessage,
  slotProblems,
  slotRewardLabel,
  slotShareNote,
  type SlotReward,
} from './mini-game-slots'

/**
 * The campaigns tab of `/loyalty-program`.
 *
 * **FB-166.** The catalogue holds fifteen types and legacy drew thirteen. `push_blast` was on no screen while
 * being declared runnable *and* notification-bearing; it has a card here. `spin_wheel` stays off the card list,
 * which is the right omission — `CampaignCatalog` says its row exists only to give the wheel a place in the
 * list, and nothing reads its `isActive`. A type no group claims is drawn anyway, so nothing can fall off this
 * screen again, and §10A.2's second campaign of a type is drawn beside the first rather than instead of it.
 *
 * **FB-167.** Every campaign reports `redeemedCount: 0` and there is no `UPDATE` of that column anywhere —
 * `sent_count` has four, `redeemed_count` appears only in `SELECT` and `INSERT` lists. So the rate is
 * *nemerané*, not 0 %. Same rule as FB-113 on `/analytics`, which reads the same column.
 *
 * **FB-168.** `cashback`'s newest run is the server **refusing** to run it, and legacy shows it as a run that
 * reached nobody — which is also what `meninova_zlava` genuinely did. The two are told apart.
 *
 * **FB-169.** The daily send cap, which legacy has no screen for at all. It is the only guard that spans
 * campaigns, it is off, and switching it on with the number at zero would leave it off — silently, because
 * `overDailyCap` returns early on both. The form refuses that pair.
 *
 * **FB-170.** The wheel's eight segments are weighted to 140, and legacy prints each weight where a
 * percentage belongs. The odds are shown instead, with the total beside them so the arithmetic is checkable,
 * and the losing chance — a third of every spin — named on its own line.
 */

export function CampaignsTab({ writable }: { writable: boolean }) {
  const { toast } = useToast()
  const queryClient = useQueryClient()

  /* §9.535, owner #80 - **from a read every manager screen already makes.** `FranchiseModulesProvider` loads
     `/franchise/modules` on mount and polls it, so this needs no request of its own; `GET /campaigns` answers a
     bare list and had no wrapper to carry it (§10A.279). The context deliberately does NOT fold this into its
     fail-open gating, so `null` means *not known* and the note appears on an explicit `false` only. */
  const { scheduledJobsEnabled } = useFranchiseModules()
  const jobsOff = scheduledJobsOffNote(scheduledJobsEnabled)
  const campaignQuery = useGetCampaigns()
  // §9.339 - the reach the SMS switch's guard quotes, from the server: `limit: '1'` because what is
  // wanted is the `total`, not the rows - asking for a page would put 2 384 people's names and
  // numbers in the browser to render one number. §9.777 - the wording is no longer read here: each
  // campaign carries its own SMS text, and `messages.defaults` says what it would send.
  const smsReachQuery = useGetCustomers({ smsOptIn: 'in', limit: 1 })
  const smsReach =
    typeof (smsReachQuery.data as { total?: unknown } | undefined)?.total === 'number'
      ? ((smsReachQuery.data as { total: number }).total)
      : 0

  const wheelQuery = useGetCampaignsWheelAdmin()
  const referralQuery = useGetReferrals()
  const capQuery = useGetCampaignsSendCap()
  // The leaderboard is per calendar month, and the page opens on the running one — §9.242 gave it
  // the setter it had always been missing. Without one the panel could only ask for a month that is
  // empty on the first of it, and empty for the whole of it on this data.
  const [month, setMonth] = useState(() => latestLeaderboardMonth(new Date()))
  const [boardView, setBoardView] = useState<'live' | 'history'>('live')
  const boardQuery = useGetLoyaltyLeaderboard({ month })
  // Asked for only when the history is being looked at, as legacy asks for it.
  const historyQuery = useGetLoyaltyLeaderboardHistory({
    query: { enabled: boardView === 'history' },
  })

  const [confirmRun, setConfirmRun] = useState<Campaign | null>(null)
  const [creating, setCreating] = useState<NewCampaign | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<Campaign | null>(null)
  const [capForm, setCapForm] = useState<{ dailySendCap: string; isActive: boolean } | null>(null)
  // §9.160 — the wheel's editor. Open means editing; the switch on the card does not need it.
  const [wheelForm, setWheelForm] = useState<WheelForm | null>(null)

  const campaigns = useMemo(() => campaignsOf(campaignQuery.data), [campaignQuery.data])
  // §9.171 — which campaign the dialog is showing. Null is the table on its own.
  const [editing, setEditing] = useState<Campaign | null>(null)
  const groups = groupedCampaigns(campaigns)
  // §9.785 - the customer cashback is no longer drawn; one still switched on is said on the organization row.
  const retiredOn = retiredCashbackStillOn(campaigns)
  const unclaimed = ungroupedCampaigns(campaigns)
  const duplicated = typesWithSeveral(campaigns)
  const wheel = wheelOf(wheelQuery.data)
  /**
   * §9.183 — how many the wheel has sent.
   *
   * From the `spin_wheel` **campaign** row, which is the one thing that row is for: the wheel's own
   * settings hold its configuration and no counter. Nought when the row is absent, rather than a
   * blank cell that would read as *not measured*.
   */
  const wheelSentCount = campaigns.find((entry) => entry.type === WHEEL_PLACEHOLDER_TYPE)?.sentCount ?? 0
  const board = leaderboardOf(boardQuery.data)
  const history = leaderboardHistoryOf(historyQuery.data)
  const referrals = referralsOf(referralQuery.data)
  const referralSummary = referralTotals(referrals)
  const cap = sendCapOf(capQuery.data)
  const capEdit = capForm ?? { dailySendCap: String(cap.dailySendCap), isActive: cap.isActive }
  const capIssues = capProblems(capEdit)

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: getGetCampaignsQueryKey() })
  }

  const toggle = usePatchCampaignsType({
    mutation: {
      onSuccess: () => {
        invalidate()
        toast({ title: tt('Kampaň uložená') })
      },
      // §9.385 - a campaign the validator refused says which field it refused.
      onError: (caught) =>
        toast({
          variant: 'destructive',
          title: tt('Uloženie zlyhalo'),
          description: apiErrorMessage(caught, tt('Na serveri sa nič nezmenilo.')),
        }),
    },
  })

  const create = usePostCampaigns({
    mutation: {
      onSuccess: () => {
        invalidate()
        setCreating(null)
        toast({ title: tt('Kampaň založená'), description: creationNote() })
      },
      onError: (error) =>
        toast({
          variant: 'destructive',
          title: tt('Kampaň sa nepodarilo založiť'),
          description: (error as { error?: string } | null)?.error ?? undefined,
        }),
    },
  })

  const remove = useDeleteCampaignsIdId({
    mutation: {
      onSuccess: () => {
        invalidate()
        setConfirmDelete(null)
        toast({ title: tt('Kampaň zmazaná') })
      },
      onError: (error) =>
        toast({
          variant: 'destructive',
          title: tt('Kampaň sa nepodarilo zmazať'),
          description: (error as { error?: string } | null)?.error ?? undefined,
        }),
    },
  })

  const run = usePostCampaignsTypeRun({
    mutation: {
      onSuccess: (answer) => {
        invalidate()
        const recipients = (answer as { recipientCount?: unknown } | undefined)?.recipientCount
        toast({
          title: tt('Kampaň spustená'),
          description:
            typeof recipients === 'number'
              ? `${tt('Príjemcov')}: ${formatCount(recipients)}`
              : tt('Server nepovedal, koľkých našla.'),
        })
      },
      // §9.385 - *"dôvod je v karte kampane"* sent the operator looking. The reason is right here.
      onError: (caught) =>
        toast({
          variant: 'destructive',
          title: tt('Spustenie zlyhalo'),
          description: apiErrorMessage(caught, tt('Server ho odmietol - dôvod je v karte kampane.')),
        }),
    },
  })

  // §9.160 — the wheel had no control at all: the card showed its odds and `is_active` sat false with
  // no way to change it. The server has always taken these four fields.
  // §9.262 — the window lives on the row's own columns, so it is patched by id.
  const saveWindow = usePatchCampaignsIdId({
    mutation: {
      onSuccess: () => {
        invalidate()
        toast({ title: tt('Platnosť uložená') })
      },
      onError: (error) =>
        toast({
          variant: 'destructive',
          title: tt('Platnosť sa neuložila'),
          description: (error as { error?: string } | null)?.error ?? undefined,
        }),
    },
  })

  // §9.785 - by id, each one, because `PATCH /campaigns/{type}` reaches only the first campaign of a type.
  const switchOffRetired = usePatchCampaignsIdId({
    mutation: {
      onSuccess: () => {
        invalidate()
        toast({ title: tt('Cashback pre zákazníka vypnutý') })
      },
      onError: (caught) =>
        toast({
          variant: 'destructive',
          title: tt('Cashback sa nepodarilo vypnúť'),
          description: apiErrorMessage(caught, tt('Na serveri sa nič nezmenilo.')),
        }),
    },
  })

  const saveWheel = usePatchCampaignsWheel({
    mutation: {
      onSuccess: () => {
        setWheelForm(null)
        void queryClient.invalidateQueries({ queryKey: getGetCampaignsWheelAdminQueryKey() })
        toast({ title: tt('Koleso šťastia uložené') })
      },
      onError: (caught) =>
        toast({
          variant: 'destructive',
          title: tt('Koleso sa neuložilo'),
          description: apiErrorMessage(caught, tt('Server zmenu odmietol.')),
        }),
    },
  })

  const saveCap = usePatchCampaignsSendCap({
    mutation: {
      onSuccess: () => {
        setCapForm(null)
        void queryClient.invalidateQueries({ queryKey: getGetCampaignsSendCapQueryKey() })
        toast({ title: tt('Denný limit uložený') })
      },
      onError: (caught) =>
        toast({
          variant: 'destructive',
          title: tt('Limit sa neuložil'),
          description: apiErrorMessage(caught, tt('Server zmenu odmietol.')),
        }),
    },
  })

  if (campaignQuery.isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Tile label={tt('Kampaní')} value={formatCount(campaigns.length)} name="count" />
        <Tile label={tt('Aktívnych')} value={formatCount(activeCount(campaigns))} name="active" />
        <Tile label={tt('Odoslaných kupónov')} value={formatCount(totalSent(campaigns))} name="sent" />
        {/* FB-167. Not 0 %. */}
        <Tile label={tt('Uplatnených')} value={redemptionLabel(campaigns)} name="redemption" />
      </div>

      {redemptionNote(campaigns) !== '' && (
        <p className="text-xs text-amber-500 flex items-start gap-2" data-redemption-note>
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          {redemptionNote(campaigns)}
        </p>
      )}

      {/* ─── FB-169: the cap that spans campaigns ─── */}
      <section className="bg-card border border-border rounded-2xl p-5 space-y-3" data-cap={capState(cap)}>
        <div className="flex items-start gap-2">
          <Gauge className="w-4 h-4 text-primary shrink-0 mt-0.5" />
          <div className="min-w-0">
            <h3 className="text-sm font-bold">
              <T>Denný limit na zákazníka</T>
            </h3>
            <p className="text-xs text-muted-foreground" data-cap-message>
              {capMessage(cap)}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <label className="space-y-1">
            <span className="text-[11px] text-muted-foreground uppercase tracking-wider block">
              <T>E-mailov denne</T>
            </span>
            <input
              value={capEdit.dailySendCap}
              onChange={(event) => setCapForm({ ...capEdit, dailySendCap: event.target.value })}
              aria-label={tt('E-mailov denne')}
              disabled={!writable}
              data-cap-input
              className="w-24 bg-background border border-border rounded-lg px-3 py-2 text-sm tabular-nums outline-none focus:ring-2 focus:ring-primary/20 disabled:opacity-50"
            />
          </label>
          <label className="flex items-center gap-2 text-xs pb-2">
            <input
              type="checkbox"
              checked={capEdit.isActive}
              onChange={(event) => setCapForm({ ...capEdit, isActive: event.target.checked })}
              aria-label={tt('Limit je zapnutý')}
              disabled={!writable}
              data-cap-active
              className="w-4 h-4 rounded accent-primary"
            />
            <T>Limit je zapnutý</T>
          </label>
          <button
            type="button"
            data-cap-save
            disabled={!writable || saveCap.isPending || capIssues.length > 0}
            onClick={() => saveCap.mutate({ data: capPayload(capEdit) })}
            className={cn('px-3 py-2 rounded-lg', BUTTON_PRIMARY, 'text-xs')}
          >
            <T>Uložiť limit</T>
          </button>
        </div>

        {capIssues.length > 0 && (
          <div className="space-y-1" data-cap-problems>
            {capIssues.map((problem) => (
              <p key={problem} className="text-xs text-red-500 flex items-start gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
                {problem}
              </p>
            ))}
          </div>
        )}

        <p className="text-[11px] text-muted-foreground" data-cap-history>
          {capHistoryNote()}
        </p>
      </section>

      {/* §9.5's multi-campaign half. `POST /campaigns` and the five `/campaigns/id/{id}` endpoints have
        * never been reachable: 15 campaigns of 15 types, so no type has a second one. */}
      {writable && (
        <section className="rounded-2xl border border-border bg-card p-4 space-y-3" data-new-campaign>
          <div className="flex items-center justify-between gap-3">
            <div>
              <h3 className="text-sm font-bold">
                <T>Ďalšia kampaň toho istého typu</T>
              </h3>
              <p className="text-xs text-muted-foreground">
                <T>
                  Jeden typ môže mať viac kampaní - napríklad meniny zvlášť na júl a zvlášť na
                  august, každá s vlastným obdobím.
                </T>
              </p>
            </div>
            {creating === null && (
              <button
                type="button"
                data-add-campaign
                onClick={() => setCreating(EMPTY_NEW_CAMPAIGN)}
                className="min-h-[40px] px-4 rounded-xl bg-primary text-primary-foreground font-bold hover:opacity-90 transition-opacity shrink-0"
              >
                <T>Založiť</T>
              </button>
            )}
          </div>

          {creating !== null && (
            <div className="space-y-2">
              <select
                value={creating.type}
                onChange={(event) => setCreating({ ...creating, type: event.target.value })}
                aria-label={tt('Typ kampane')}
                data-new-campaign-type
                className="w-full min-h-[44px] rounded-xl bg-background border border-border px-3 text-sm outline-none focus:border-primary"
              >
                <option value="">{tt('- vyberte typ -')}</option>
                {/* §9.785 - not the retired customer cashback, which nothing would draw once created. */}
                {creatableTypes().map((type) => (
                  <option key={type} value={type}>
                    {campaignName(type)}
                  </option>
                ))}
              </select>
              <input
                value={creating.name}
                onChange={(event) => setCreating({ ...creating, name: event.target.value })}
                placeholder={tt('Názov, napr. Meniny - júl')}
                aria-label={tt('Názov kampane')}
                data-new-campaign-name
                className="w-full min-h-[44px] rounded-xl bg-background border border-border px-3 text-sm outline-none focus:border-primary"
              />
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="datetime-local"
                  value={creating.startsAt}
                  onChange={(event) => setCreating({ ...creating, startsAt: event.target.value })}
                  aria-label={tt('Začiatok')}
                  data-new-campaign-starts
                  className="min-h-[44px] rounded-xl bg-background border border-border px-3 text-sm outline-none focus:border-primary"
                />
                <input
                  type="datetime-local"
                  value={creating.endsAt}
                  onChange={(event) => setCreating({ ...creating, endsAt: event.target.value })}
                  aria-label={tt('Koniec')}
                  data-new-campaign-ends
                  className="min-h-[44px] rounded-xl bg-background border border-border px-3 text-sm outline-none focus:border-primary"
                />
              </div>

              {newCampaignProblems(creating).length > 0 && (
                <div className="space-y-1" data-new-campaign-problems>
                  {newCampaignProblems(creating).map((problem) => (
                    <p key={problem} className="text-xs text-red-500 flex items-start gap-1.5">
                      <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
                      {problem}
                    </p>
                  ))}
                </div>
              )}

              <p className="text-[11px] text-muted-foreground" data-new-campaign-note>
                {creationNote()}
              </p>

              <div className="flex gap-2">
                <button
                  type="button"
                  data-cancel-campaign
                  onClick={() => setCreating(null)}
                  className="min-h-[44px] px-4 rounded-xl border border-border text-sm font-semibold"
                >
                  <T>Zrušiť</T>
                </button>
                <button
                  type="button"
                  data-save-campaign
                  disabled={newCampaignProblems(creating).length > 0 || create.isPending}
                  onClick={() => create.mutate({ data: newCampaignPayload(creating) })}
                  className={cn('flex-1 min-h-[44px] rounded-xl', BUTTON_PRIMARY)}
                >
                  <T>Založiť kampaň</T>
                </button>
              </div>
            </div>
          )}
        </section>
      )}

      {duplicated.length > 0 && (
        <p className="text-xs text-muted-foreground" data-duplicate-types={duplicated.join(',')}>
          <T>Niektoré typy majú viac kampaní</T> ({duplicated.map(campaignName).join(', ')}) -{' '}
          <T>zobrazené sú všetky, každá pod svojím názvom.</T>
        </p>
      )}

      {/* §9.171 - a table, as the owner asked twice. The grouping survives as a header row rather
          than as four separate grids: the groups are what tell somebody *when* a campaign fires, and
          losing them to get a table would trade one complaint for another. Everything that can be
          edited moved into the dialog, so a row is a row. */}
      {/* One sentence for the whole table, because this is one fact about the installation: with the
          scheduler off, *Nikdy nebežala* on every scheduled row has a single cause. §9.534's per-row note is
          the other half and stays per row, because *nothing can send this type* is about the feature. */}
      {jobsOff !== null && (
        <p className="text-xs text-amber-500 flex items-start gap-2" data-campaign-jobs-off>
          <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <T>{jobsOff}</T>
        </p>
      )}

      <div className="rounded-2xl border border-border bg-card overflow-hidden" data-campaigns-table>
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="text-left font-semibold px-4 py-2.5">
                <T>Kampaň</T>
              </th>
              <th className="text-left font-semibold px-4 py-2.5 hidden lg:table-cell">
                <T>Obdobie</T>
              </th>
              <th className="text-right font-semibold px-4 py-2.5 hidden md:table-cell">
                <T>Odoslaných</T>
              </th>
              <th className="text-left font-semibold px-4 py-2.5 hidden md:table-cell">
                <T>Výsledok</T>
              </th>
              <th className="text-center font-semibold px-4 py-2.5">
                <T>Zapnutá</T>
              </th>
              <th className="text-right font-semibold px-4 py-2.5">
                <T>Akcie</T>
              </th>
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => (
              <Fragment key={group.key}>
                <tr data-group={group.key} className="bg-muted/20">
                  <td colSpan={6} className="px-4 py-2">
                    <span className="text-xs font-bold">{groupTitle(group.key)}</span>
                    <span className="ml-2 text-[11px] text-muted-foreground">
                      {groupDescription(group.key)}
                    </span>
                  </td>
                </tr>
                {group.campaigns.map((campaign) => (
                  <CampaignRow
                    key={campaign.id}
                    campaign={campaign}
                    writable={writable}
                    busy={toggle.isPending || run.isPending}
                    onToggle={() =>
                      toggle.mutate({ type: campaign.type, data: { isActive: !campaign.isActive } })
                    }
                    onOpen={() => setEditing(campaign)}
                  />
                ))}
                {/* §9.785, owner O138g - the *Cashback* row is the organizations' now, last in its group. */}
                {group.key === ORGANIZATION_CASHBACK_GROUP && (
                  <OrganizationCashbackRow
                    writable={writable}
                    retiredOn={retiredOn.length > 0}
                    switchingOff={switchOffRetired.isPending}
                    onSwitchOffRetired={() => {
                      for (const one of retiredOn) {
                        switchOffRetired.mutate({ id: one.id, data: { isActive: false } })
                      }
                    }}
                  />
                )}
              </Fragment>
            ))}
            {/* §9.183 - the wheel, in the table with the rest. Its row is **not** the `spin_wheel`
                campaign row: that row's `is_active` is inert and nothing in the backend reads it, so
                drawing it as an ordinary campaign would put a switch on screen that changes a column
                nobody reads. Every control here drives `wheel_campaign_settings`; the sent count is
                the one thing the campaign row is for, which is exactly what it exists for. */}
            {wheel !== null && (
              <tr
                data-wheel={wheel.isActive ? 'on' : 'off'}
                className="border-t border-border hover:bg-muted/30 transition-colors"
              >
                <td className="px-4 py-2.5 align-top">
                  <p className="font-semibold">{campaignName('spin_wheel')}</p>
                  <p className="text-xs text-muted-foreground" data-wheel-note>
                    {wheelNote(wheel)}
                  </p>
                  {/* Two settings that decide who spins and what a win is worth, and are not on the
                      wheel itself. */}
                  <p className="text-xs text-muted-foreground" data-wheel-conditions>
                    {wheelConditions(wheel)}
                  </p>
                  {wheelLoseChance(wheel) !== null && (
                    <p className="text-xs text-amber-500" data-wheel-lose>
                      <T>Šanca nevyhrať nič</T>: {formatPercent(wheelLoseChance(wheel) ?? 0, 1)}
                    </p>
                  )}
                </td>
                <td className="px-4 py-2.5 align-top text-xs text-muted-foreground hidden lg:table-cell">
                  {/* The odds per segment, which is what a wheel's *period* column is worth showing:
                      legacy prints the weight, and a weight reads as a percentage when it is not. */}
                  <div className="space-y-0.5">
                    {wheelOdds(wheel).map((entry, index) => (
                      <div key={index} className="flex items-center gap-2" data-segment={index}>
                        <span
                          className="w-2.5 h-2.5 rounded-full shrink-0"
                          style={{ backgroundColor: entry.segment.color }}
                        />
                        <span className="flex-1 truncate">{entry.segment.label}</span>
                        {/* The weight, kept: `data-wheel-note` claims to be their total, and a note
                            that disagrees with the rows under it is the bug worth catching. */}
                        <span className="text-muted-foreground shrink-0">
                          <T>váha</T> {formatCount(entry.segment.weight)}
                        </span>
                        <span className="font-semibold tabular-nums" data-segment-odds={index}>
                          {formatPercent(entry.percent, 1)}
                        </span>
                      </div>
                    ))}
                  </div>
                </td>
                <td className="px-4 py-2.5 align-top text-right tabular-nums" data-wheel-sent>
                  {formatCount(wheelSentCount)}
                </td>
                <td className="px-4 py-2.5 align-top hidden md:table-cell" />
                <td className="px-4 py-2.5 align-top text-center">
                  {/* §9.160's switch, moved. Switching it on is one decision and needs no form. */}
                  {/* §9.199 - the same switch every other row in this table has. §9.183 moved the
                      wheel into the table and brought its old panel control with it: a bare
                      checkbox in a column of pill toggles, which is what the owner saw. */}
                  <button
                    type="button"
                    role="switch"
                    aria-checked={wheel.isActive}
                    aria-label={tt('Koleso šťastia je zapnuté')}
                    disabled={!writable || saveWheel.isPending}
                    data-wheel-active
                    data-toggle-state={wheel.isActive ? 'on' : 'off'}
                    onClick={() => saveWheel.mutate({ data: { isActive: !wheel.isActive } })}
                    className={cn(
                      'w-11 h-6 rounded-full relative shrink-0 transition-colors disabled:opacity-50',
                      wheel.isActive ? 'bg-primary' : 'bg-muted',
                    )}
                  >
                    <span
                      className={cn(
                        'absolute top-0.5 w-5 h-5 rounded-full bg-background transition-all',
                        wheel.isActive ? 'left-[22px]' : 'left-0.5',
                      )}
                    />
                  </button>
                </td>
                <td className="px-4 py-2.5 align-top text-right">
                  <button
                    type="button"
                    onClick={() => setWheelForm(wheelFormOf(wheel))}
                    disabled={!writable}
                    data-wheel-edit
                    className="px-3 py-1.5 rounded-lg border border-border text-xs font-bold hover:bg-muted disabled:opacity-50"
                  >
                    <T>Upraviť</T>
                  </button>
                </td>
              </tr>
            )}
            {unclaimed.length > 0 && (
              <Fragment>
                <tr data-unclaimed={unclaimed.length} className="bg-amber-500/10">
                  <td colSpan={6} className="px-4 py-2">
                    <span className="text-xs font-bold">
                      <T>Kampane, ktoré táto obrazovka nepozná</T>
                    </span>
                    <span className="ml-2 text-[11px] text-amber-500">
                      <T>
                        Server ich vrátil, ale nepatria do žiadnej skupiny - zobrazené sú, aby sa
                        nestratili.
                      </T>
                    </span>
                  </td>
                </tr>
                {unclaimed.map((campaign) => (
                  <CampaignRow
                    key={campaign.id}
                    campaign={campaign}
                    writable={writable}
                    busy={toggle.isPending}
                    onToggle={() =>
                      toggle.mutate({ type: campaign.type, data: { isActive: !campaign.isActive } })
                    }
                    onOpen={() => setEditing(campaign)}
                  />
                ))}
              </Fragment>
            )}
          </tbody>
        </table>
      </div>

      {editing !== null && (
        <CampaignDialog
          campaign={editing}
          writable={writable}
          smsRecipients={smsReach}
          busy={toggle.isPending || run.isPending}
          onClose={() => setEditing(null)}
          onToggle={() =>
            toggle.mutate({ type: editing.type, data: { isActive: !editing.isActive } })
          }
          onSaveSettings={(settings) =>
            toggle.mutate({ type: editing.type, data: { settings } })
          }
          // §9.262 — by **id**, not by type. `PATCH /campaigns/{type}` reaches the primary campaign
          // of that type, so a window set on the second Black Friday would land on the first.
          onSaveWindow={(payload) => saveWindow.mutate({ id: editing.id, data: payload })}
          onRun={() => setConfirmRun(editing)}
          onDelete={mayDelete(editing, campaigns) ? () => setConfirmDelete(editing) : null}
          // §9.35 — `deleteRefusalNote` was written with `mayDelete` and never called, so a primary
          // campaign simply had no delete button and no reason given.
          deleteRefusal={mayDelete(editing, campaigns) ? null : deleteRefusalNote()}
        />
      )}

      <section className="bg-card border border-border rounded-2xl p-5 space-y-3" data-leaderboard-panel>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h3 className="text-sm font-bold flex items-center gap-2">
            <Trophy className="w-4 h-4 text-primary" /> {tt('Rebríček')}
          </h3>
          <div className="flex items-center gap-2">
            {/* Legacy's two views, in the same panel - §9.242. */}
            {(['live', 'history'] as const).map((view) => (
              <button
                key={view}
                type="button"
                data-board-view={view}
                aria-pressed={boardView === view}
                onClick={() => setBoardView(view)}
                className={cn(
                  'px-3 py-1 rounded-lg text-xs font-semibold border',
                  boardView === view
                    ? 'bg-primary/15 border-primary/40 text-primary'
                    : 'border-border text-muted-foreground',
                )}
              >
                {view === 'live' ? <T>Aktuálny</T> : <T>História</T>}
              </button>
            ))}
          </div>
        </div>

        {boardView === 'live' && (
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <T>Mesiac</T>
            <input
              type="month"
              data-leaderboard-month
              value={month}
              // A month that has not happened can only be empty; a past one is the data's answer.
              max={latestLeaderboardMonth(new Date())}
              onChange={(event) => {
                const chosen = event.target.value
                if (isLeaderboardMonth(chosen)) setMonth(chosen)
              }}
              className="px-2 py-1 rounded-lg border border-border bg-background text-foreground"
            />
          </label>
        )}

        {boardView === 'history' && (
          <div data-leaderboard-history={history.length}>
            {history.length === 0 ? (
              <EmptyState
                icon={<Trophy className="w-8 h-8" />}
                title={tt('Zatiaľ žiadne vyhodnotenie')}
                // `leaderboard_snapshots` is empty on this data, and saying *why* is the difference
                // between an empty screen and a broken one. No button here: awarding a month sends.
                description={tt('Rebríček ešte nebol ani raz vyhodnotený, takže história je prázdna.')}
              />
            ) : (
              <div className="divide-y divide-border">
                {history.map((entry) => (
                  <div key={entry.month} className="py-3 space-y-1.5" data-history-month={entry.month}>
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      {entry.month}
                    </p>
                    {entry.winners.map((winner) => (
                      <div key={winner.id} className="flex items-center gap-3 text-sm">
                        <span className="w-6 text-muted-foreground tabular-nums">{winner.rank}.</span>
                        <span className="flex-1 truncate">
                          {winner.customerName}
                          {winner.customerEmail !== null && (
                            <span className="text-xs text-muted-foreground ml-2">
                              {winner.customerEmail}
                            </span>
                          )}
                        </span>
                        <span className="tabular-nums font-semibold">
                          {formatCents(winner.totalSpentCents)}
                        </span>
                        {/* What they were actually given - the live board has no equivalent. */}
                        {winner.couponCode !== null && (
                          <span
                            className="text-[11px] font-mono px-2 py-0.5 rounded bg-muted"
                            data-history-coupon
                          >
                            {winner.couponCode}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {boardView === 'live' && board.length === 0 ? (
          <EmptyState
            icon={<Trophy className="w-8 h-8" />}
            title={tt('Za tento mesiac nikto')}
            description={tt('Rebríček počíta tržbu za kalendárny mesiac, takže prvého je prázdny.')}
          />
        ) : boardView === 'live' ? (
          <div className="divide-y divide-border" data-leaderboard={board.length}>
            {board.slice(0, 10).map((row) => (
              <div
                key={row.customerId}
                className="flex items-center gap-3 py-2 text-sm"
                data-rank={row.rank}
              >
                <span className="w-6 text-muted-foreground tabular-nums">{row.rank}.</span>
                <span className="flex-1 truncate">{row.customerName}</span>
                <span className="tabular-nums font-semibold">{formatCents(row.totalSpentCents)}</span>
                <span className="text-xs text-muted-foreground tabular-nums">
                  {formatCount(row.pointsBalance)} <T>b.</T>
                </span>
              </div>
            ))}
          </div>
        ) : null}
      </section>

      <section
        className="bg-card border border-border rounded-2xl p-5 space-y-2"
        data-referrals={referralSummary.count}
      >
        <h3 className="text-sm font-bold flex items-center gap-2">
          <Users className="w-4 h-4 text-primary" /> <T>Odporúčania</T>
        </h3>
        <p className="text-xs text-muted-foreground">
          {formatCount(referralSummary.count)} <T>odporúčaní</T> ·{' '}
          {formatCount(referralSummary.points)} <T>bodov</T> ·{' '}
          {formatCents(referralSummary.discountCents)} <T>zľavy</T>
        </p>
        {!referralStatusesWorthFiltering(referrals) && referralSummary.count > 0 && (
          <p className="text-[11px] text-muted-foreground" data-referral-statuses>
            <T>Všetky majú rovnaký stav</T> ({referralSummary.statuses.join(', ')}) -{' '}
            <T>filtrovať sa tu nedá po čom.</T>
          </p>
        )}
      </section>

      <ConfirmDialog
        open={confirmDelete !== null}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => {
          if (confirmDelete !== null) remove.mutate({ id: confirmDelete.id })
        }}
        title={tt('Zmazať kampaň?')}
        description={
          confirmDelete === null
            ? ''
            : `${campaignLabel(confirmDelete)} - ${tt('história odoslaní zostane, samotná kampaň zmizne.')}`
        }
        confirmLabel={tt('Zmazať')}
        variant="danger"
        loading={remove.isPending}
      />

      {/* §9.160 - the wheel's editor. A modal, which is what the owner asked this screen to use. */}
      {wheelForm !== null && wheel !== null && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          data-wheel-dialog
        >
          <div className="bg-card border border-border rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-auto">
            <div className="px-5 py-4 border-b border-border flex items-center justify-between">
              <h3 className="font-bold text-sm">
                <T>Koleso šťastia</T>
              </h3>
              <button
                type="button"
                onClick={() => setWheelForm(null)}
                aria-label={tt('Zavrieť')}
                className="p-1.5 rounded-lg hover:bg-muted"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div className="flex flex-wrap gap-3">
                <label className="space-y-1">
                  <span className="text-[11px] text-muted-foreground uppercase tracking-wider block">
                    <T>Minimálna objednávka (€)</T>
                  </span>
                  <input
                    value={wheelForm.minOrder}
                    onChange={(event) => setWheelForm({ ...wheelForm, minOrder: event.target.value })}
                    aria-label={tt('Minimálna objednávka (€)')}
                    data-wheel-min
                    className="w-32 bg-background border border-border rounded-lg px-3 py-2 text-sm tabular-nums"
                  />
                </label>
                <label className="space-y-1">
                  <span className="text-[11px] text-muted-foreground uppercase tracking-wider block">
                    <T>Výhra platí (hodín)</T>
                  </span>
                  <input
                    value={wheelForm.rewardValidityHours}
                    onChange={(event) =>
                      setWheelForm({ ...wheelForm, rewardValidityHours: event.target.value })
                    }
                    aria-label={tt('Výhra platí (hodín)')}
                    data-wheel-hours
                    className="w-32 bg-background border border-border rounded-lg px-3 py-2 text-sm tabular-nums"
                  />
                </label>
              </div>

              {/* The odds are computed from the weights and shown while they are typed - you set a
                * weight, the screen says what it means. Typing odds directly would let them sum to
                * something other than a hundred. */}
              <div className="space-y-2">
                {wheelForm.segments.map((segment, index) => (
                  <div key={index} className="flex items-center gap-2" data-wheel-segment={index}>
                    <input
                      type="color"
                      value={segment.color}
                      onChange={(event) => {
                        const segments = [...wheelForm.segments]
                        segments[index] = { ...segment, color: event.target.value }
                        setWheelForm({ ...wheelForm, segments })
                      }}
                      aria-label={`${tt('Farba')} ${index + 1}`}
                      className="w-8 h-8 rounded border border-border shrink-0 bg-background"
                    />
                    <input
                      value={segment.label}
                      onChange={(event) => {
                        const segments = [...wheelForm.segments]
                        segments[index] = { ...segment, label: event.target.value }
                        setWheelForm({ ...wheelForm, segments })
                      }}
                      aria-label={`${tt('Názov políčka')} ${index + 1}`}
                      className="flex-1 min-w-0 bg-background border border-border rounded-lg px-3 py-2 text-sm"
                    />
                    <input
                      value={segment.weight}
                      onChange={(event) => {
                        const segments = [...wheelForm.segments]
                        segments[index] = { ...segment, weight: event.target.value }
                        setWheelForm({ ...wheelForm, segments })
                      }}
                      aria-label={`${tt('Váha')} ${index + 1}`}
                      className="w-20 bg-background border border-border rounded-lg px-3 py-2 text-sm tabular-nums"
                    />
                    <span
                      className="w-16 text-right text-xs font-semibold tabular-nums text-muted-foreground"
                      data-wheel-odds={index}
                    >
                      {formatPercent(weightOdds(wheelForm)[index] ?? 0, 1)}
                    </span>
                  </div>
                ))}
              </div>

              {wheelProblems(wheelForm).map((problem, index) => (
                <p key={index} className="text-xs text-destructive" data-wheel-problem>
                  {wheelProblemMessage(problem)}
                </p>
              ))}
            </div>

            <div className="px-5 py-4 border-t border-border flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setWheelForm(null)}
                className="px-4 py-2 rounded-lg border border-border text-sm font-semibold hover:bg-muted"
              >
                <T>Zrušiť</T>
              </button>
              <button
                type="button"
                data-wheel-save
                disabled={wheelProblems(wheelForm).length > 0 || saveWheel.isPending}
                onClick={() => saveWheel.mutate({ data: wheelPayload(wheelForm) })}
                className={cn(BUTTON_PRIMARY, 'px-4 py-2 rounded-lg text-sm disabled:opacity-50')}
              >
                <T>Uložiť</T>
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmRun !== null}
        onClose={() => setConfirmRun(null)}
        title={tt('Spustiť kampaň teraz?')}
        description={
          confirmRun === null
            ? ''
            : `${campaignLabel(confirmRun)}: ${triggerMessage(triggerOf(confirmRun.type))} ${tt('Beh sa nedá vrátiť a zákazníkom môže odísť správa.')}`
        }
        confirmLabel={tt('Spustiť')}
        icon={<Play className="w-5 h-5" />}
        loading={run.isPending}
        onConfirm={() => {
          const campaign = confirmRun
          setConfirmRun(null)
          if (campaign !== null) run.mutate({ type: campaign.type, data: {} })
        }}
      />
    </div>
  )
}

function Tile({ label, value, name }: { label: string; value: string; name: string }) {
  return (
    <div className="bg-card border border-border rounded-xl px-4 py-3" data-tile={name}>
      <p className="text-[11px] text-muted-foreground uppercase tracking-wider">{label}</p>
      <p className="text-lg font-bold tabular-nums mt-0.5">{value}</p>
    </div>
  )
}

/**
 * One row of the campaigns table — §9.171.
 *
 * What a row can do without opening anything: read the campaign, see whether it is on, and turn it
 * on or off. Everything that changes a *setting* is in the dialog, because a table whose cells are
 * all inputs is the grid of cards this replaced with lines drawn round it.
 */
function CampaignRow({
  campaign,
  writable,
  busy,
  onToggle,
  onOpen,
}: {
  campaign: Campaign
  writable: boolean
  busy: boolean
  onToggle: () => void
  onOpen: () => void
}) {
  const trigger = triggerOf(campaign.type)
  const outcome = runOutcome(campaign)
  const window = campaignWindow(campaign)

  return (
    <tr
      data-campaign={campaign.type}
      data-campaign-id={campaign.id}
      data-campaign-active={campaign.isActive ? 'yes' : 'no'}
      className="border-t border-border hover:bg-muted/30 transition-colors"
    >
      <td className="px-4 py-2.5 align-top">
        <p className="font-semibold">
          {campaignLabel(campaign)}
          {!isNamedCampaign(campaign.type) && (
            <span className="ml-1.5 text-[10px] font-normal text-amber-500">
              <T>neznámy typ</T>
            </span>
          )}
        </p>
        <p className="text-xs text-muted-foreground" data-trigger={trigger}>
          {trigger === 'configured'
            ? tornadoTriggerMessage(campaign.settings)
            : triggerMessage(trigger)}
        </p>
        {hasSegment(campaign) && (
          <p className="text-[11px] text-muted-foreground" data-segmented>
            <T>Zúžená na časť zákazníkov.</T>
          </p>
        )}
      </td>
      <td className="px-4 py-2.5 align-top text-xs text-muted-foreground hidden lg:table-cell">
        {window === null ? (
          '-'
        ) : (
          <span data-window>
            {window.from === null ? '-' : formatDateTime(window.from)} →{' '}
            {window.to === null ? tt('bez konca') : formatDateTime(window.to)}
          </span>
        )}
      </td>
      <td className="px-4 py-2.5 align-top text-right tabular-nums" data-sent>
        {formatCount(campaign.sentCount)}
      </td>
      <td className="px-4 py-2.5 align-top hidden md:table-cell max-w-[16rem]">
        <StatusBadge label={outcomeLabel(outcome)} color={outcomeColor(outcome)} />
        {/* FB-168 - a refusal and a run that matched nobody are different things, and *which* is
            what the column is for. It stays on the row rather than moving into the dialog with the
            editable half: reading a result must not need a click. */}
        <p
          className={cn(
            'text-[11px] mt-0.5',
            outcome === 'refused' ? 'text-amber-500' : 'text-muted-foreground',
          )}
          data-run-outcome={outcome}
        >
          {runMessage(campaign)}
        </p>
        {campaign.lastRun !== null && (
          <p className="text-[11px] text-muted-foreground mt-0.5">
            {formatDateTime(campaign.lastRun.ranAt)}
          </p>
        )}
      </td>
      <td className="px-4 py-2.5 align-top text-center">
        <button
          type="button"
          role="switch"
          aria-checked={campaign.isActive}
          aria-label={campaignLabel(campaign)}
          disabled={busy || !writable}
          data-toggle-state={campaign.isActive ? 'on' : 'off'}
          onClick={onToggle}
          className={cn(
            'w-11 h-6 rounded-full relative shrink-0 transition-colors disabled:opacity-50',
            campaign.isActive ? 'bg-primary' : 'bg-muted',
          )}
        >
          <span
            className={cn(
              'absolute top-0.5 w-5 h-5 rounded-full bg-background transition-all',
              campaign.isActive ? 'left-[22px]' : 'left-0.5',
            )}
          />
        </button>
      </td>
      <td className="px-4 py-2.5 align-top text-right">
        <button
          type="button"
          data-campaign-edit={campaign.type}
          onClick={onOpen}
          className="px-3 py-1.5 rounded-lg border border-border text-xs font-bold hover:bg-muted"
        >
          <T>Upraviť</T>
        </button>
      </td>
    </tr>
  )
}

/**
 * Everything a campaign can be changed by — §9.171, in a dialog rather than on a card.
 *
 * The body is the card this replaced, unchanged: the same settings fields, the same *Spustiť teraz*,
 * the same delete and its refusal, and the same `data-*` hooks, so what the e2e asserts about a
 * campaign still exists — it is reached by opening the row rather than by scrolling to it.
 */
function CampaignDialog({
  campaign,
  writable,
  busy,
  onClose,
  onToggle,
  onSaveSettings,
  onSaveWindow,
  onRun,
  onDelete = null,
  deleteRefusal = null,
  smsRecipients = 0,
}: {
  campaign: Campaign
  writable: boolean
  busy: boolean
  /**
   * §9.339 - how many customers the SMS channel can reach, from the server rather than counted here:
   * the per-channel figure the manual sender's chips quote. A screen that counted its own page would
   * say 50.
   */
  smsRecipients?: number
  onClose: () => void
  onToggle: () => void
  onSaveSettings: (settings: Record<string, unknown>) => void
  /** §9.262 — `PATCH /campaigns/id/{id}` with both bounds, so a cleared one is cleared. */
  onSaveWindow: (payload: Record<string, unknown>) => void
  onRun: () => void
  /** Null on the first campaign of a type — the server refuses to delete that one. */
  onDelete?: (() => void) | null
  /** Why this one cannot be deleted, when it cannot — §9.35. */
  deleteRefusal?: string | null
}) {
  const trigger = triggerOf(campaign.type)
  const outcome = runOutcome(campaign)
  const window = campaignWindow(campaign)

  // §9.141 — the settings this campaign carries, as editable fields. Keyed by the campaign so a
  // draft does not survive onto a different card.
  // §9.434 - the type, so `kuponove_tornado` is offered its `trigger` before one is stored.
  // §9.777 - what the campaign says to a customer; null for one that sends nothing.
  const messages = campaign.messages
  const fields = useMemo(
    () =>
      campaignFields(campaign.settings, campaign.type).filter(
        // §9.777 - the texts are drawn in *Správa zákazníkovi*, not as a database key in the grid.
        // §9.780 - and the by-hand ones under *Pri ručnom poslaní*.
        (field) =>
          messages === null ||
          !([...MESSAGE_TEXT_KEYS, ...BY_HAND_TEXT_KEYS] as readonly string[]).includes(field.key),
      ),
    [campaign.settings, campaign.type, messages],
  )
  const [draft, setDraft] = useState<Record<string, string>>({})
  // §9.262, owner O9 — *"we should set, when it is available, when it is not available… from
  // when-to when"*. The columns have always existed and `POST /campaigns` has always taken them;
  // nothing could change them afterwards, so the fifteen campaigns that already exist — Black
  // Friday among them — could never be given one at all.
  const [windowDraft, setWindowDraft] = useState<CampaignWindowForm>(() => windowFormOf(campaign))
  const windowIssues = windowProblems(windowDraft)
  const windowDirty = windowChanged(campaign, windowDraft)
  // Read once on open rather than on every render: `Date.now()` in a render body is impure, and the
  // note only has to be right for the moment the drawer was opened.
  const [openedAt] = useState(() => Date.now())
  const runningNote = windowNote(campaign, openedAt)
  const parsed = fields.map((field) => {
    const typed = draft[field.key]
    return {
      field,
      value: typed === undefined ? field.value : parseFieldValue(field.kind, typed),
    }
  })
  const invalidKeys = parsed.filter((row) => row.value === null).map((row) => row.field.key)
  const edited: Record<string, string | number | boolean> = {}
  for (const row of parsed) {
    if (row.value !== null) edited[row.field.key] = row.value
  }
  // §9.310 - the channels, which are the one setting this editor can *create*. Seeded from the
  // server's own fallback so an untouched campaign shows what it is actually doing.
  const [channels, setChannels] = useState(() => channelsOf(campaign.settings))
  /* §9.481 - the mini-game's prize slots, the third setting this editor can create. Invisible until now
     because `campaignFields` drops any value that is an object and `slots` is a list, so the richest
     setting either game has had no control at all while legacy has a full editor for it. */
  const isMiniGame = MINI_GAME_TYPES.includes(campaign.type)
  const [slotDraft, setSlotDraft] = useState(() => slotFormOf(campaign.settings))
  const slotIssues = isMiniGame ? slotProblems(slotDraft) : []
  // §9.339 - the count and the cost come from the server's own per-channel reach, the same figure
  // the manual sender's chips quote. A screen that counted its own page would say 50, which is
  // FB-157's defect, and a cost quoted off it would understate the bill thirty-fold.
  const [askingSms, setAskingSms] = useState(false)
  const [smsTyped, setSmsTyped] = useState('')
  const smsReach = smsRecipients
  // §9.777 - the texts somebody typed and the mode, drafts like every other setting here.
  const [texts, setTexts] = useState<Partial<Record<MessageTextKey, string>>>({})
  // §9.780 - the texts of a send by hand, drafted the same way.
  const [byHandTexts, setByHandTexts] = useState<Partial<Record<MessageTextKey, string>>>({})
  const [mode, setMode] = useState<ChannelMode>(() => channelModeOf(campaign.settings))
  const tickedChannels =
    messages === null ? [] : messages.channels.filter((channel) => channels[channel])
  // Only what moved: the server merges, so sending everything back would overwrite keys this screen
  // never showed.
  // §9.436 - the type, so a setting the campaign does not yet store can still be saved. Without it
  // `trigger` was dropped from the payload and the save reported success.
  const pendingSettings = {
    ...changedSettings(
      campaign.settings,
      edited,
      channels,
      campaign.type,
      // §9.481 - only the two mini-games; every other type has no slots and must not start writing one.
      isMiniGame ? slotDraft : undefined,
    ),
    ...(messages === null ? {} : changedMessages(campaign, texts, mode, byHandTexts)),
  }
  const dirty = Object.keys(pendingSettings).length > 0
  const hidden = unshownSettingsNote(campaign.settings)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        className="absolute inset-0 bg-black/50"
        aria-label={tt('Zavrieť')}
        onClick={onClose}
      />
      <section
        className="relative w-full max-w-lg max-h-[85vh] overflow-y-auto rounded-2xl border border-border bg-background p-5 space-y-2"
        data-campaign-dialog={campaign.type}
        data-campaign-id={campaign.id}
        data-campaign-active={campaign.isActive ? 'yes' : 'no'}
      >
        <div className="flex justify-end -mt-1 -mr-1">
          <button
            type="button"
            data-campaign-close
            aria-label={tt('Zavrieť')}
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-sm">
            {campaignLabel(campaign)}
            {!isNamedCampaign(campaign.type) && (
              <span className="ml-1.5 text-[10px] font-normal text-amber-500">
                <T>neznámy typ</T>
              </span>
            )}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5" data-trigger={trigger}>
            {trigger === 'configured'
            ? tornadoTriggerMessage(campaign.settings)
            : triggerMessage(trigger)}
          </p>
          {window !== null && (
            <p className="text-[11px] text-muted-foreground mt-0.5" data-window>
              {window.from === null ? '-' : formatDateTime(window.from)} →{' '}
              {window.to === null ? tt('bez konca') : formatDateTime(window.to)}
            </p>
          )}
          {hasSegment(campaign) && (
            <p className="text-[11px] text-muted-foreground" data-segmented>
              <T>Zúžená na časť zákazníkov.</T>
            </p>
          )}
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={campaign.isActive}
          aria-label={campaignLabel(campaign)}
          disabled={busy || !writable}
          data-toggle-state={campaign.isActive ? 'on' : 'off'}
          onClick={onToggle}
          className={cn(
            'w-11 h-6 rounded-full relative shrink-0 transition-colors disabled:opacity-50',
            campaign.isActive ? 'bg-primary' : 'bg-muted',
          )}
        >
          <span
            className={cn(
              'absolute top-0.5 w-5 h-5 rounded-full bg-background transition-all',
              campaign.isActive ? 'left-[22px]' : 'left-0.5',
            )}
          />
        </button>
      </div>

      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <span data-sent>
          {formatCount(campaign.sentCount)} <T>odoslaných</T>
        </span>
        <StatusBadge label={outcomeLabel(outcome)} color={outcomeColor(outcome)} />
        {campaign.lastRun !== null && (
          <span className="ml-auto">{formatDateTime(campaign.lastRun.ranAt)}</span>
        )}
      </div>

      {/* FB-168's outcome line lives on the table row - §9.171. Repeating it here would give the
          same `data-run-outcome` two homes, and a selector that matches both is a strict-mode
          failure waiting for whoever writes the next spec. */}

      {/* §9.141 - the settings the screen could never edit. `PATCH /campaigns/{type}` has always
        * accepted them; the tab sent `{isActive}` and nothing else, which is both *nedajú sa
        * nastavovať* and *upraviť moneyback*. Fields come from the campaign's own settings, so all
        * fifteen types are editable rather than the three somebody would have hand-written. */}
      {/* §9.262 - when the campaign is available, and when it stops being. Above the settings grid
        * because it decides whether any of them apply: `CampaignService.active()` filters on
        * `withinWindow` before it reads a single setting. */}
      <div className="space-y-2 pt-2 border-t border-border" data-campaign-window={campaign.type}>
        <p className="text-[11px] font-semibold text-muted-foreground">
          <T>Kedy kampaň platí</T>
        </p>
        <div className="grid grid-cols-2 gap-2">
          <label className="text-[11px] text-muted-foreground space-y-0.5">
            <span className="block font-semibold">
              <T>Od</T>
            </span>
            <input
              type="datetime-local"
              data-window-starts
              disabled={!writable}
              value={windowDraft.startsAt}
              onChange={(event) => setWindowDraft((current) => ({ ...current, startsAt: event.target.value }))}
              className="w-full min-h-[32px] rounded-lg border border-border bg-background px-2 text-xs"
            />
          </label>
          <label className="text-[11px] text-muted-foreground space-y-0.5">
            <span className="block font-semibold">
              <T>Do</T>
            </span>
            <input
              type="datetime-local"
              data-window-ends
              disabled={!writable}
              value={windowDraft.endsAt}
              onChange={(event) => setWindowDraft((current) => ({ ...current, endsAt: event.target.value }))}
              className="w-full min-h-[32px] rounded-lg border border-border bg-background px-2 text-xs"
            />
          </label>
        </div>
        {/* An empty bound is not a mistake: most campaigns run until somebody switches them off, and
          * a screen that demanded both would make that the hard case. */}
        <p className="text-[11px] text-muted-foreground" data-window-hint>
          <T>Prázdne pole znamená bez hranice - kampaň beží, kým ju nevypnete.</T>
        </p>
        {runningNote !== '' && (
          <p className="text-[11px] text-amber-500" data-window-warning>
            {runningNote}
          </p>
        )}
        {windowIssues.length > 0 && (
          <p className="text-[11px] text-red-500" data-window-invalid>
            {windowIssues.join(' · ')}
          </p>
        )}
        <button
          type="button"
          data-save-window={campaign.type}
          disabled={busy || !writable || windowIssues.length > 0 || !windowDirty}
          onClick={() => onSaveWindow(windowPayload(windowDraft))}
          className="px-3 py-1.5 rounded-lg border border-border text-xs font-bold disabled:opacity-50"
        >
          <T>Uložiť platnosť</T>
        </button>
      </div>

      {(fields.length > 0 || messages !== null) && (
        <div className="space-y-2 pt-2 border-t border-border" data-campaign-settings={campaign.type}>
          <div className="grid grid-cols-2 gap-2">
            {fields.map((field) => (
              <label key={field.key} className="text-[11px] text-muted-foreground space-y-0.5">
                {/* §9.273, owner O13 - this printed the raw `settings` key, so the editor read
                  * `minOrderValue`, `cashbackPercent`, `couponValidityHours`. An unknown key keeps
                  * its own name and says so, rather than being given a Slovak one it has not
                  * earned. */}
                <span className="block font-semibold" data-setting-label={field.key}>
                  {campaignFieldLabel(field.key)}
                </span>
                {!isKnownField(field.key) && (
                  <span className="block text-[10px] text-amber-500" data-setting-unknown>
                    <T>Neznáme nastavenie - zobrazený je názov z databázy.</T>
                  </span>
                )}
                {/* §9.276 - a setting the server stores and never reads. Said under the field rather
                  * than hidden: hiding it would lose the only trace that the row carries it. */}
                {inertNote(campaign.type, field.key) !== '' && (
                  <span className="block text-[10px] text-amber-500" data-setting-inert={field.key}>
                    {inertNote(campaign.type, field.key)}
                  </span>
                )}
                {field.kind === 'choice' ? (
                  /* §9.434 - the owner's ask, as three events rather than three slugs. A `select`
                   * rather than a text box: the server narrows an unrecognised word back to the
                   * card, and it would do that silently. */
                  <select
                    data-setting={field.key}
                    disabled={!writable}
                    value={String(draft[field.key] ?? field.value)}
                    onChange={(event) =>
                      setDraft((current) => ({ ...current, [field.key]: event.target.value }))
                    }
                    className="w-full min-h-[32px] rounded-lg border border-border bg-background px-2 text-xs"
                  >
                    {TORNADO_TRIGGERS.map((choice) => (
                      <option key={choice} value={choice}>
                        {tornadoTriggerLabel(choice)}
                      </option>
                    ))}
                  </select>
                ) : field.kind === 'boolean' ? (
                  <input
                    type="checkbox"
                    data-setting={field.key}
                    disabled={!writable}
                    checked={String(draft[field.key] ?? field.value) === 'true'}
                    onChange={(event) =>
                      setDraft((current) => ({ ...current, [field.key]: String(event.target.checked) }))
                    }
                    className="h-4 w-4 rounded border-border"
                  />
                ) : (
                  <input
                    type={field.kind === 'number' ? 'number' : field.kind === 'time' ? 'time' : 'text'}
                    data-setting={field.key}
                    disabled={!writable}
                    value={String(draft[field.key] ?? field.value)}
                    onChange={(event) =>
                      setDraft((current) => ({ ...current, [field.key]: event.target.value }))
                    }
                    className="w-full min-h-[32px] rounded-lg border border-border bg-background px-2 text-xs"
                  />
                )}
              </label>
            ))}
          </div>
          {/* §9.777, owner O138 - what the campaign says and how. Only the channels its sender can use,
            * in the order the default mode tries them, and the texts of each ticked channel showing
            * what would be sent until somebody writes their own. A campaign that sends nothing draws
            * none of this: §9.310 drew the ticks on every campaign and they did something on one. */}
          {messages !== null && (
            <div className="space-y-2 border-t border-border pt-3" data-campaign-message={campaign.type}>
              <p className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
                <T>Správa zákazníkovi</T>
              </p>
              <div className="flex flex-wrap gap-3" data-campaign-channels>
                {messages.channels.map((channel) => (
                  <label key={channel} className="flex items-center gap-1.5 text-xs">
                    <input
                      type="checkbox"
                      data-campaign-channel={channel}
                      checked={channels[channel]}
                      disabled={!writable}
                      onChange={(event) => {
                        // §9.339 - turning SMS **on** is the expensive click, so it goes through a
                        // confirmation; turning it off does not. A toll on the corrective action is
                        // the wrong asymmetry, which is `SAAS.md` §3.3's reasoning for *Žiadne*.
                        if (channel === 'sms' && event.target.checked) {
                          setAskingSms(true)
                          return
                        }
                        setChannels((current) => ({ ...current, [channel]: event.target.checked }))
                      }}
                      className="w-3.5 h-3.5 rounded accent-primary"
                    />
                    <T>{campaignFieldLabelForChannel(channel)}</T>
                  </label>
                ))}
              </div>
              {/* With one channel there is nothing to be first among or to send together. */}
              {messages.channels.length > 1 && (
                <label className="flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
                  <span className="font-semibold">
                    <T>Ako sa kanály použijú</T>
                  </span>
                  <select
                    data-channel-mode
                    value={mode}
                    disabled={!writable}
                    onChange={(event) => setMode(event.target.value === 'all' ? 'all' : 'fallback')}
                    className="min-h-[28px] rounded-lg border border-border bg-background px-2 text-xs"
                  >
                    {(['fallback', 'all'] as const).map((choice) => (
                      <option key={choice} value={choice}>
                        {channelModeLabel(choice)}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              {tickedChannels.length === 0 && (
                <p className="text-[11px] text-red-500" data-no-channel>
                  <T>Žiadny kanál nie je zapnutý - kampaň nikoho neosloví.</T>
                </p>
              )}

            {/* §9.339, and `BACKEND.md` §10A.116's reasoning: *"what needs the count, the cost and
              * the typed confirmation is turning the SMS channel on for a campaign, because that is
              * the irreversible decision - after it, every trigger costs money quietly."* */}
            {askingSms && (
              <div
                className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 space-y-2"
                data-sms-confirm
              >
                <p className="text-xs font-semibold" data-sms-warning>
                  {smsSwitchWarning(smsReach)}
                </p>
                <p className="text-[11px] text-muted-foreground">
                  <T>Na potvrdenie napíšte</T>{' '}
                  <span className="font-mono font-bold">{smsConfirmPhrase()}</span>
                </p>
                <input
                  value={smsTyped}
                  onChange={(event) => setSmsTyped(event.target.value)}
                  aria-label={tt('Potvrdzovacia fráza')}
                  data-sms-phrase
                  className="w-full px-2 py-1.5 rounded-lg border border-border bg-background text-sm"
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    data-sms-cancel
                    onClick={() => {
                      setAskingSms(false)
                      setSmsTyped('')
                    }}
                    className="flex-1 px-2 py-1.5 rounded-lg border border-border text-xs font-semibold"
                  >
                    <T>Zrušiť</T>
                  </button>
                  <button
                    type="button"
                    data-sms-enable
                    disabled={!smsConfirmed(smsTyped)}
                    onClick={() => {
                      setChannels((current) => ({ ...current, sms: true }))
                      setAskingSms(false)
                      setSmsTyped('')
                    }}
                    className="flex-1 px-2 py-1.5 rounded-lg border border-amber-500/50 bg-amber-500/20 text-xs font-bold disabled:opacity-40"
                  >
                    <T>Zapnúť SMS</T>
                  </button>
                </div>
              </div>
            )}

              {tickedChannels.map((channel) => (
                <div key={channel} className="space-y-1.5 rounded-xl border border-border p-2.5" data-message-channel={channel}>
                  <p className="text-[11px] font-bold">
                    <T>{campaignFieldLabelForChannel(channel)}</T>
                  </p>
                  {messageTextKeys(channel).map((key) => (
                    <MessageTextBox
                      key={key}
                      textKey={key}
                      value={texts[key] ?? messageText(campaign, key)}
                      fallback={messages.defaults[key] ?? ''}
                      writable={writable}
                      byHand={false}
                      onChange={(typed) => setTexts((current) => ({ ...current, [key]: typed }))}
                    />
                  ))}
                </div>
              ))}
              {messages.placeholders.length > 0 && (
                <p className="text-[11px] text-muted-foreground" data-message-placeholders>
                  <T>Do textov môžete vložiť</T>: {placeholderList(messages.placeholders)}
                </p>
              )}
              {/* §9.780, owner question #191 - an order reward sent by hand says its own words: the texts
                * above thank for an order, which a coupon sent by hand did not follow. */}
              {hasByHandTexts(messages) && tickedChannels.length > 0 && (
                <div className="space-y-2 border-t border-border pt-3" data-message-by-hand>
                  <p className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
                    <T>Pri ručnom poslaní</T>
                  </p>
                  <p className="text-[11px] text-muted-foreground">
                    <T>Tieto texty dostane zákazník, keď kampaň pošlete cez Poslať teraz. Texty vyššie ďakujú za objednávku.</T>
                  </p>
                  {tickedChannels.map((channel) => (
                    <div
                      key={channel}
                      className="space-y-1.5 rounded-xl border border-border p-2.5"
                      data-by-hand-channel={channel}
                    >
                      <p className="text-[11px] font-bold">
                        <T>{campaignFieldLabelForChannel(channel)}</T>
                      </p>
                      {messageTextKeys(channel).map((key) => (
                        <MessageTextBox
                          key={key}
                          textKey={key}
                          value={byHandTexts[key] ?? byHandText(campaign, key)}
                          fallback={messages.byHandDefaults[key] ?? ''}
                          writable={writable}
                          byHand
                          onChange={(typed) => setByHandTexts((current) => ({ ...current, [key]: typed }))}
                        />
                      ))}
                    </div>
                  ))}
                  {messages.byHandPlaceholders.length > 0 && (
                    <p className="text-[11px] text-muted-foreground" data-by-hand-placeholders>
                      <T>Do textov môžete vložiť</T>: {placeholderList(messages.byHandPlaceholders)}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* §9.310, FB-486 - a setting this editor cannot draw is named rather than dropped. A
            * screen showing four of a campaign's six settings is worse than one showing four and
            * saying there are six. */}
          {hidden !== '' && (
            <p className="text-[11px] text-amber-500" data-settings-unshown>
              <T>{hidden}</T>
            </p>
          )}

          {invalidKeys.length > 0 && (
            <p className="text-[11px] text-red-500" data-setting-invalid>
              <T>Neplatná hodnota</T>: {invalidKeys.join(', ')}
            </p>
          )}

          {/* §9.481 - the prize slots. Only the two mini-games carry them, and legacy's editor is the
              shape: a row per slot, a delete on each, an add below, and the share stated rather than
              demanded. Every rule here is BACKEND.md §10A.241's, said at the field rather than as a
              toast after the save. */}
          {isMiniGame && (
            <div className="space-y-1.5 pt-2 border-t border-border" data-slots={campaign.type}>
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-semibold">
                  <T>Výherné políčka</T>
                </span>
                <span className="text-[11px] text-muted-foreground" data-slot-share>
                  {slotShareNote(slotDraft)}
                </span>
              </div>
              <div className="grid grid-cols-[1fr_84px_60px_64px_24px] gap-1 text-[10px] text-muted-foreground">
                <span><T>Popis</T></span>
                <span><T>Typ odmeny</T></span>
                <span><T>Hodnota</T></span>
                <span><T>Pravdep. %</T></span>
                <span />
              </div>
              {slotDraft.rows.map((row, index) => (
                <div
                  key={index}
                  className="grid grid-cols-[1fr_84px_60px_64px_24px] gap-1 items-center"
                  data-slot-row={String(index)}
                >
                  <input
                    aria-label={tt('Popis políčka')}
                    value={row.label}
                    onChange={(event) =>
                      setSlotDraft((previous) => ({
                        rows: previous.rows.map((one, position) =>
                          position === index ? { ...one, label: event.target.value } : one,
                        ),
                      }))
                    }
                    className="min-h-[28px] rounded border border-border bg-background px-1.5 text-[11px]"
                  />
                  <select
                    aria-label={tt('Typ odmeny')}
                    value={row.reward}
                    onChange={(event) =>
                      setSlotDraft((previous) => ({
                        rows: previous.rows.map((one, position) =>
                          position === index
                            ? { ...one, reward: event.target.value as SlotReward }
                            : one,
                        ),
                      }))
                    }
                    className="min-h-[28px] rounded border border-border bg-background px-1 text-[11px]"
                  >
                    {SLOT_REWARDS.map((reward) => (
                      <option key={reward} value={reward}>
                        {slotRewardLabel(reward)}
                      </option>
                    ))}
                  </select>
                  <input
                    aria-label={tt('Hodnota odmeny')}
                    inputMode="decimal"
                    value={row.value}
                    /* Legacy disables this for its no-win slot, and so does this: a value there is
                       refused, so offering the box would invite the refusal. */
                    disabled={row.reward === 'none'}
                    onChange={(event) =>
                      setSlotDraft((previous) => ({
                        rows: previous.rows.map((one, position) =>
                          position === index ? { ...one, value: event.target.value } : one,
                        ),
                      }))
                    }
                    className="min-h-[28px] rounded border border-border bg-background px-1.5 text-[11px] disabled:opacity-40"
                  />
                  <input
                    aria-label={tt('Pravdepodobnosť')}
                    inputMode="decimal"
                    value={row.probability}
                    onChange={(event) =>
                      setSlotDraft((previous) => ({
                        rows: previous.rows.map((one, position) =>
                          position === index ? { ...one, probability: event.target.value } : one,
                        ),
                      }))
                    }
                    className="min-h-[28px] rounded border border-border bg-background px-1.5 text-[11px]"
                  />
                  <button
                    type="button"
                    data-slot-remove={String(index)}
                    aria-label={tt('Odstrániť políčko')}
                    onClick={() =>
                      setSlotDraft((previous) => ({
                        rows: previous.rows.filter((_, position) => position !== index),
                      }))
                    }
                    className="w-6 h-6 rounded bg-red-500/10 text-red-500 text-[11px]"
                  >
                    ✕
                  </button>
                </div>
              ))}
              <button
                type="button"
                data-slot-add
                onClick={() => setSlotDraft((previous) => ({ rows: [...previous.rows, newSlot()] }))}
                className="px-2 py-1 rounded-lg border border-border text-[11px] font-semibold"
              >
                <T>+ Pridať políčko</T>
              </button>
              {slotIssues.length > 0 && (
                <ul className="space-y-0.5" data-slot-problems>
                  {slotIssues.map((problem, position) => (
                    <li key={position} className="text-[10px] text-red-500">
                      {slotProblemMessage(problem)}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          <button
            type="button"
            data-save-settings={campaign.type}
            disabled={busy || !writable || invalidKeys.length > 0 || slotIssues.length > 0 || !dirty}
            onClick={() => onSaveSettings(pendingSettings)}
            className="px-3 py-1.5 rounded-lg border border-border text-xs font-bold disabled:opacity-50"
          >
            <T>Uložiť nastavenia</T>
          </button>
        </div>
      )}

      {/* §9.778, owner O138d - send it now, to everybody or a chosen kind of customer. */}
      {sendKindOf(campaign.type) !== null && (
        <SendNowPanel
          campaignId={campaign.id}
          type={campaign.type}
          kind={sendKindOf(campaign.type) ?? 'announcement'}
          channels={(messages?.channels ?? []).filter(
            (channel) => channelsOf(campaign.settings)[channel],
          )}
          writable={writable}
          unsaved={dirty}
        />
      )}

      {mayRunByHand(campaign.type) ? (
        <button
          type="button"
          data-run={campaign.type}
          disabled={busy || !writable}
          onClick={onRun}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-xs font-medium disabled:opacity-50"
        >
          <Play className="w-3 h-3" /> <T>Spustiť teraz</T>
        </button>
      ) : (
        <p
          className="text-[11px] text-muted-foreground flex items-center gap-1.5"
          data-no-run={campaign.type}
        >
          <Info className="w-3 h-3 shrink-0" />
          <T>Ručne sa spustiť nedá - server to odmietne.</T>
        </p>
      )}

      {onDelete === null && deleteRefusal !== null && writable && (
        <p className="text-[11px] text-muted-foreground" data-delete-refused={campaign.id}>
          {deleteRefusal}
        </p>
      )}

      {onDelete !== null && writable && (
        <button
          type="button"
          data-delete-campaign={campaign.id}
          disabled={busy}
          onClick={onDelete}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-xs font-medium text-destructive disabled:opacity-50"
        >
          <Trash2 className="w-3 h-3" /> <T>Zmazať</T>
        </button>
      )}
      </section>
    </div>
  )
}

function outcomeLabel(outcome: RunOutcome): string {
  if (outcome === 'refused') return tt('odmietnuté')
  if (outcome === 'never') return tt('nikdy')
  if (outcome === 'reached-nobody') return tt('nikoho')
  if (outcome === 'partial') return tt('čiastočne')
  return tt('doručené')
}

function outcomeColor(outcome: RunOutcome): 'amber' | 'emerald' | 'zinc' {
  if (outcome === 'refused' || outcome === 'partial') return 'amber'
  if (outcome === 'delivered') return 'emerald'
  return 'zinc'
}
